import { getQueue, mutate, queuePartition, withdraw, type Ack, type MutateOptions } from './api'
import { addDaysISO, todayISO } from './ui'

// One message about a customer's jobs, as the changes it made (several jobs; Astra r1 finding 8, r2 findings 1 and
// 2). A message to Messages is several saves: each job's count, and each pencilled (or moved) visit. They are kept
// here, by the save's own key, on this phone and for this account, so ONE Undo, or "Didn't send it" later, reverses
// exactly that message: every count and every visit, and nothing else.
//
// A save that waits for signal is known by its key until it lands; api.ts announces the landing (even after a
// reload, when the queue sends it again under the same key), and the record learns the row. Reversing a save that
// has never been sent takes it back out of the queue; one sent but not yet answered is reversed the moment it lands.

export type EffectKind = 'count' | 'pencil' | 'move'

export interface Effect {
  kind: EffectKind
  key: string | null // the save's own key (null until it is made)
  path: string // where the reverse goes: the counted row's PATCH path, or /api/cleans/:id once the visit is known
  id: number | null // the visit made or moved (pencil, move)
  version: number | null // the row's version after this save, once it landed
  before: Record<string, unknown> | null // the fields as they were (count: the counters; move: the date)
  done: boolean // reversed (or taken back)
}

export interface MessageRecord {
  id: string
  customerId: number
  on: string // the day it was sent
  jobs: number[] // the extra jobs it was about (the main job's count is a 'count' effect on the customer)
  main: boolean // it counted the main job too
  effects: Effect[]
  undo: boolean // reversal asked for; effects still on their way are reversed when they land
}

const STORE = 'roundly:message-records'

function storeKey(): string | null {
  const ns = queuePartition()
  return ns === null ? null : `${STORE}:${ns}`
}

function readAll(): MessageRecord[] {
  const k = storeKey()
  if (k === null) return []
  try {
    const list = JSON.parse(localStorage.getItem(k) ?? '[]') as MessageRecord[]
    const oldest = addDaysISO(todayISO(), -14)
    return Array.isArray(list) ? list.filter((r) => r.on >= oldest) : []
  } catch {
    return []
  }
}

function writeAll(list: MessageRecord[]): void {
  const k = storeKey()
  if (k === null) return
  try {
    if (list.length) localStorage.setItem(k, JSON.stringify(list))
    else localStorage.removeItem(k)
  } catch {
    // Storage refused: the Undo bar still works while this page is open; a reload falls back to the job's own put-back.
  }
}

function save(rec: MessageRecord): void {
  writeAll([...readAll().filter((r) => r.id !== rec.id), rec])
}

export function startRecord(customerId: number, on: string, jobs: number[], main: boolean): MessageRecord {
  const rec: MessageRecord = { id: `${customerId}:${on}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`, customerId, on, jobs, main, effects: [], undo: false }
  save(rec)
  return rec
}

// The newest message kept for this customer sent on that day.
export function recordFor(customerId: number, on: string | null): MessageRecord | null {
  if (!on) return null
  return readAll().filter((r) => r.customerId === customerId && r.on === on && !r.undo).at(-1) ?? null
}

// Make one of the message's saves, kept on the record whichever way it goes (now, or from the queue later).
// Added to the record as it is kept on the phone (not this screen's copy of it), so a save made after an earlier one
// has landed (a visit booked once the count is in, bug hunt 8 Oct 2026, server-data #8) never writes over what the
// record has learnt since.
export function tracked(rec: MessageRecord, kind: EffectKind, before: Record<string, unknown> | null, opts: MutateOptions): Promise<Ack> {
  const effect: Effect = { kind, key: null, path: opts.path, id: null, version: null, before, done: false }
  const kept = readAll().find((r) => r.id === rec.id) ?? rec
  const at = kept.effects.length
  kept.effects.push(effect)
  save(kept)
  rec.effects = kept.effects
  rec.undo = kept.undo
  return mutate({
    ...opts,
    body: { ...(opts.body as Record<string, unknown>), message_record_id: rec.id },
    onQueued: (m) => {
      effect.key = m.key
      const fresh = readAll().find((r) => r.id === rec.id)
      if (fresh?.effects[at]) {
        fresh.effects[at]!.key = m.key
        save(fresh)
      }
      opts.onQueued?.(m)
    },
    onAck: (ack) => {
      const fresh = readAll().find((r) => r.id === rec.id) ?? (rec.effects[at] === effect ? rec : undefined)
      if (fresh?.effects[at]) noteRow(fresh, at, ack.row)
      opts.onAck?.(ack)
    },
  })
}

// A visit put on the day before the message went (the Diary's To place, then its "Ask … about …" pop-up): already
// saved, so it joins the record as landed, and the message's Undo, or "Didn't send it" later, takes it back off exactly
// as the Diary's own Undo would (a visit edited or agreed since is left alone). Bug hunt 8 Oct 2026, server-data #9.
export function addPlaced(rec: MessageRecord, kind: 'pencil' | 'move', id: number, version: number, before: Record<string, unknown> | null): void {
  const kept = readAll().find((r) => r.id === rec.id) ?? rec
  kept.effects.push({ kind, key: null, path: `/api/cleans/${id}`, id, version, before, done: false })
  save(kept)
  rec.effects = kept.effects
}

function noteRow(rec: MessageRecord, i: number, row: unknown): void {
  const e = rec.effects[i]!
  const r = row as { id?: unknown; version?: unknown } | null
  if (typeof r?.version === 'number') e.version = r.version
  if ((e.kind === 'pencil' || e.kind === 'move') && typeof r?.id === 'number') {
    e.id = r.id
    e.path = `/api/cleans/${r.id}`
  }
  save(rec)
  if (rec.undo && !e.done) reverseEffect(rec, i)
}

// A save of some kept message landed from the queue: the record learns its row (and reverses it when asked to).
if (typeof window !== 'undefined') {
  window.addEventListener('roundly:queued-landed', (ev) => {
    const d = (ev as CustomEvent<{ key: string; row: unknown }>).detail
    if (!d?.key) return
    for (const rec of readAll()) {
      const i = rec.effects.findIndex((e) => e.key === d.key)
      if (i >= 0) {
        noteRow(rec, i, d.row)
        return
      }
    }
  })
}

function reverseEffect(rec: MessageRecord, i: number, versions?: ReadonlyMap<string, number>): Promise<unknown> | undefined {
  const e = rec.effects[i]!
  if (e.done) return
  // Never sent: taken back out of the queue, and nothing reaches the server.
  if (e.key && getQueue().some((q) => q.key === e.key) && withdraw(e.key)) {
    e.done = true
    save(rec)
    return
  }
  if (e.version === null) return // on its way: reversed when it lands (noteRow)
  e.done = true
  save(rec)
  // -1: left alone on purpose (a visit agreed or done since; never removed by putting a message back).
  if (versions?.get(e.path) === -1) return
  // A count put back later ("Didn't send it") goes at the row's version as the screen has it now.
  if (e.kind === 'count') return mutate({ label: 'Undo message count', method: 'PATCH', path: e.path, body: { version: versions?.get(e.path) ?? e.version, ...(e.before ?? {}) } })
  // undo_restore: the server puts back what the move replaced, from its own record of it (functions/lib/cleans.ts
  // restoredByUndo): a week with no day chosen, Roundly's suggestion, a "Couldn't do it" record (bug hunt 8 Oct 2026,
  // server-data #4). The Diary's own move Undo asks the same.
  if (e.kind === 'move') return mutate({ label: 'Undo move', method: 'PATCH', path: e.path, body: { version: versions?.get(e.path) ?? e.version, ...(e.before ?? {}), undo_restore: true } })
  return mutate({ label: 'Undo pencilled visit', method: 'DELETE', path: e.path, body: { version: versions?.get(e.path) ?? e.version } })
}

// Reverse the whole message: every count and every visit it made. Saves still on their way are taken back, or
// reversed the moment they land.
// Settles once every reverse sent now has its answer, so the screen reloads from the put-back rows and not from the
// moment before (8 Oct 2026: Undo of a send, then a change on the same card, was refused as out of date).
export function reverseRecord(rec: MessageRecord, versions?: ReadonlyMap<string, number>): Promise<void> {
  const fresh = readAll().find((r) => r.id === rec.id) ?? rec
  fresh.undo = true
  save(fresh)
  const sent = fresh.effects.map((_, i) => reverseEffect(fresh, i, versions)).filter((p): p is Promise<unknown> => !!p)
  return Promise.allSettled(sent).then(() => undefined)
}

// ---- a save that must only follow another -------------------------------------------------------------------------
// "Mark as lapsed" with "Also cancel their visit", tapped with no signal (bug hunt 8 Oct 2026, server-data #7). The
// cancel waits, kept on this phone, until the lapse itself has landed: a lapse refused because the customer changed on
// another phone never lands, so their visit is never cancelled for it (Astra). Undo before then drops the cancel; after
// it, reverses it (taken back out of the queue, or put back once it lands).

interface FollowOn {
  id: string
  after: string // the queue key of the save it follows
  on: string // the day it was asked for (kept a fortnight, like a message record)
  save: { label: string; path: string; body: Record<string, unknown> } // a PATCH
  reverse: Record<string, unknown> // the fields its Undo puts back
  made: boolean
  key: string | null
  version: number | null
  undo: boolean
}

const FOLLOW = 'roundly:follow-on'
function followKey(): string | null {
  const ns = queuePartition()
  return ns === null ? null : `${FOLLOW}:${ns}`
}
function readFollow(): FollowOn[] {
  const k = followKey()
  if (k === null) return []
  try {
    const list = JSON.parse(localStorage.getItem(k) ?? '[]') as FollowOn[]
    const oldest = addDaysISO(todayISO(), -14)
    return Array.isArray(list) ? list.filter((f) => f.on >= oldest) : []
  } catch {
    return []
  }
}
function writeFollow(list: FollowOn[]): void {
  const k = followKey()
  if (k === null) return
  try {
    if (list.length) localStorage.setItem(k, JSON.stringify(list))
    else localStorage.removeItem(k)
  } catch {
    // Storage refused: the follow-on is held for this page only.
  }
}
// Kept in memory too, so a phone that refuses storage still makes the cancel while the page is open.
const followMemory = new Map<string, FollowOn>()
function keepFollow(f: FollowOn | null, id: string): void {
  if (f) followMemory.set(id, f); else followMemory.delete(id)
  writeFollow([...readFollow().filter((x) => x.id !== id), ...(f ? [f] : [])])
}
function findFollow(id: string): FollowOn | null {
  return readFollow().find((f) => f.id === id) ?? followMemory.get(id) ?? null
}

function reverseFollow(f: FollowOn): void {
  if (f.key && getQueue().some((q) => q.key === f.key) && withdraw(f.key)) { keepFollow(null, f.id); return }
  if (f.version === null) { keepFollow({ ...f, undo: true }, f.id); return } // on its way: put back when it lands
  keepFollow(null, f.id)
  void mutate({ label: `Undo: ${f.save.label}`, method: 'PATCH', path: f.save.path, body: { version: f.version, ...f.reverse } })
}

function landedFollow(id: string, row: unknown): void {
  const f = findFollow(id)
  if (!f) return
  const version = (row as { version?: unknown } | null)?.version
  const next = { ...f, version: typeof version === 'number' ? version : f.version }
  if (next.undo) reverseFollow({ ...next, undo: false }); else keepFollow(next, id)
}

function makeFollow(f: FollowOn): void {
  keepFollow({ ...f, made: true }, f.id)
  void mutate({
    label: f.save.label,
    method: 'PATCH',
    path: f.save.path,
    body: f.save.body,
    onQueued: (m) => { const now = findFollow(f.id); if (now) keepFollow({ ...now, key: m.key }, f.id) },
    onAck: (ack) => { if (ack.ok) landedFollow(f.id, ack.row); else keepFollow(null, f.id) },
    onConflict: () => keepFollow(null, f.id),
  })
}

// Make `save` once the save under `afterKey` has landed. Returns the id its Undo takes.
export function followAfter(afterKey: string, save: FollowOn['save'], reverse: Record<string, unknown>): string {
  const id = `${afterKey}:follow`
  keepFollow({ id, after: afterKey, on: todayISO(), save, reverse, made: false, key: null, version: null, undo: false }, id)
  return id
}

// Undo: drop it before it is made, or reverse it once it was.
export function undoFollowOn(id: string): void {
  const f = findFollow(id)
  if (!f) return
  if (!f.made) { keepFollow(null, id); return }
  reverseFollow(f)
}

if (typeof window !== 'undefined') {
  window.addEventListener('roundly:queued-landed', (ev) => {
    const d = (ev as CustomEvent<{ key: string; row: unknown }>).detail
    if (!d?.key) return
    const all = [...new Map([...followMemory.values(), ...readFollow()].map((f) => [f.id, f])).values()]
    for (const f of all) {
      if (f.after === d.key && !f.made && !f.undo) makeFollow(f)
      else if (f.after === d.key && !f.made && f.undo) keepFollow(null, f.id)
      else if (f.key === d.key) landedFollow(f.id, d.row)
    }
  })
}
